import { FormHelperText, IconButton, TextField } from "@mui/material";
import { Plus, X } from "lucide-react";
import { useFieldArray, useFormContext } from "react-hook-form";

import { Button } from "@/shared/foundation/primitives";

import { CreateCustomFieldFormValues } from "./createCustomFieldForm";
import styles from "./EnumOptionsEditor.module.css";

/**
 * Editor for an enum field's allowed values. Renders one text input per option
 * with add/remove controls. Validation (non-empty, unique, length, at-least-one)
 * lives in the form schema and surfaces inline per row.
 */
export function EnumOptionsEditor() {
  const { control, register, formState } =
    useFormContext<CreateCustomFieldFormValues>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "enum_values",
  });

  return (
    <div className={styles.section}>
      <span className={styles.label}>Enum Options</span>
      <FormHelperText>
        Options cannot be changed after the field is created.
      </FormHelperText>
      <div className={styles.rows}>
        {fields.map((field, index) => {
          const error = formState.errors.enum_values?.[index]?.value;
          return (
            <div key={field.id} className={styles.row}>
              <TextField
                placeholder={`Option ${index + 1}`}
                size="small"
                fullWidth
                error={Boolean(error)}
                helperText={error?.message}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    // Add another option instead of submitting the form.
                    event.preventDefault();
                    append({ value: "" });
                  }
                }}
                {...register(`enum_values.${index}.value`)}
              />
              {fields.length > 1 ? (
                <IconButton
                  className={styles.remove}
                  aria-label={`Remove option ${index + 1}`}
                  size="small"
                  onClick={() => remove(index)}
                >
                  <X size={16} />
                </IconButton>
              ) : null}
            </div>
          );
        })}
      </div>
      <Button
        className={styles.addButton}
        variant="text"
        startIcon={<Plus size={16} />}
        onClick={() => append({ value: "" })}
      >
        Add option
      </Button>
    </div>
  );
}
